import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  GitBranch,
  Megaphone,
} from 'lucide-react';
import { NostrPublishingView } from './NostrPublishingView';
import { RepositoryInfo } from './RepositoryInfo';
import { AdvancedGitDialog } from './AdvancedGitDialog';
import { useGit } from '@/hooks/useGit';

interface GitManagementDialogProps {
  projectId: string;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultTab?: string;
}

export function GitManagementDialog({
  projectId,
  children,
  open,
  onOpenChange,
  defaultTab = 'nostr',
}: GitManagementDialogProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [remoteUrl, setRemoteUrl] = useState<string | null>(null);
  const { git } = useGit();
  const projectPath = `/projects/${projectId}`;

  useEffect(() => {
    const loadRemoteUrl = async () => {
      try {
        const url = await git.getRemoteURL(projectPath, 'origin');
        setRemoteUrl(url);
      } catch {
        setRemoteUrl(null);
      }
    };
    if (open) {
      loadRemoteUrl();
    }
  }, [open, git, projectPath]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {children && <DialogTrigger asChild>{children}</DialogTrigger>}
        <DialogContent className="max-w-2xl h-[600px] p-0 flex flex-col gap-0 overflow-hidden">
          {/* Fixed Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" />
                Repository
              </DialogTitle>
              <AdvancedGitDialog projectId={projectId} />
            </div>
          </DialogHeader>

          {/* Tabs Container - takes remaining space */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              {/* Fixed Tab List */}
              <div className="border-b px-6 shrink-0">
                <TabsList className="h-auto p-0 bg-transparent">
                  <TabsTrigger
                    value="nostr"
                    className="gap-2 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                  >
                    <Megaphone className="h-4 w-4" />
                    Nostr Publishing
                  </TabsTrigger>
                  <TabsTrigger
                    value="git"
                    className="gap-2 data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                  >
                    <GitBranch className="h-4 w-4" />
                    Git Management
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Nostr Publishing Tab */}
              <TabsContent
                value="nostr"
                className="m-0 mt-0 data-[state=active]:flex data-[state=active]:flex-1 data-[state=active]:flex-col overflow-hidden"
              >
                <ScrollArea className="flex-1">
                  <div className="p-6">
                    <NostrPublishingView projectId={projectId} remoteUrl={remoteUrl} />
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Git Management Tab */}
              <TabsContent
                value="git"
                className="m-0 mt-0 data-[state=active]:flex data-[state=active]:flex-1 data-[state=active]:flex-col overflow-hidden"
              >
                <ScrollArea className="flex-1">
                  <div className="p-6">
                    <RepositoryInfo projectId={projectId} remoteUrl={remoteUrl} />
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
